import React, { useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { KycProofUploader } from "@/components/kyc/KycProofUploader";
import { toast } from "sonner";
import { MessagesSquare, LinkIcon, Loader2, Send } from "lucide-react";

const ROLE_FB = { preacher: "Prédicateur", artist: "Artiste", influencer: "Influenceur(e)" };

// Member's answer to an admin « complément demandé » on a KYC request.
export const KycComplementForm = ({ req, onDone }) => {
  const { t } = useApp();
  const [link, setLink] = useState("");
  const [docs, setDocs] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const roleLabel = t(`role.${req.requested_role}`, ROLE_FB[req.requested_role] || req.requested_role);
  const send = async () => {
    if (!link.trim() && !docs.length && !msg.trim()) {
      toast.error(t("kyc.complement_empty", "Ajoute au moins un document, un lien ou un message."));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/kyc/${req.id}/complete`, { documents: docs, website: link.trim() || undefined, notes: msg.trim() || undefined });
      toast.success(t("kyc.complement_sent", "Complément envoyé ✓ — un administrateur va réexaminer ta demande."));
      onDone?.();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "#93C5FD", background: "#EFF6FF" }} data-testid="kyc-complement-form">
      <div className="flex items-start gap-2">
        <MessagesSquare size={18} className="shrink-0 mt-0.5" style={{ color: "#1E40AF" }} />
        <div className="min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#1E40AF" }}>{t("kyc.complement_title", "Complément demandé pour ta demande {role}").replace("{role}", roleLabel)}</div>
          <p className="text-[13px] mt-0.5" data-testid="kyc-complement-message">« {req.info_message} »</p>
        </div>
      </div>
      <div>
        <Label className="text-xs flex items-center gap-1"><LinkIcon size={12} /> {t("kyc.complement_link", "Lien (chaîne, page, réseau)")}</Label>
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" data-testid="kyc-complement-link" />
      </div>
      <div>
        <Label className="text-xs">{t("kyc.complement_docs", "Document (image)")}</Label>
        <KycProofUploader docs={docs} onChange={setDocs} buttonLabel={t("kyc.complement_add_doc", "Ajouter un justificatif")} testId="kyc-complement" />
      </div>
      <div>
        <Label className="text-xs">{t("kyc.complement_msg", "Message pour l'administrateur")}</Label>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder={t("kyc.complement_msg_ph", "Explique ou précise ce qui est demandé…")} data-testid="kyc-complement-notes" />
      </div>
      <Button className="pill-primary rounded-full w-full" onClick={send} disabled={busy} data-testid="kyc-complement-submit">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} className="mr-1" /> {t("kyc.complement_submit", "Envoyer le complément")}</>}
      </Button>
    </div>
  );
};
